import { computed, defineComponent, provide, reactive, ref, toRef } from 'vue'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import advancedFormat from 'dayjs/plugin/advancedFormat.js'
import localeData from 'dayjs/plugin/localeData.js'
import weekOfYear from 'dayjs/plugin/weekOfYear.js'
import weekYear from 'dayjs/plugin/weekYear.js'
import dayOfYear from 'dayjs/plugin/dayOfYear.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js'
import { useLocale, useNamespace } from '@element-plus/hooks'
import {
  CommonPicker,
  DEFAULT_FORMATS_DATE,
  DEFAULT_FORMATS_DATEPICKER,
} from '@element-plus/components/time-picker'
import { isFunction } from '@element-plus/utils'
import { ROOT_PICKER_INJECTION_KEY } from './constants'

import { datePickerProps } from './props/date-picker'
import { getPanel } from './panel-utils'
import type { Dayjs } from 'dayjs'
import type { DatePickerExpose } from './instance'

dayjs.extend(localeData)
dayjs.extend(advancedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)
dayjs.extend(dayOfYear)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export default defineComponent({
  name: 'ElDatePicker',
  install: null,
  props: datePickerProps,
  emits: ['update:modelValue'],
  setup(props, { expose, emit, slots }) {
    const { lang } = useLocale()
    const ns = useNamespace('picker-panel')
    const nsDate = useNamespace('date-editor')

    provide('ElPopperOptions', reactive(toRef(props, 'popperOptions')))
    provide(ROOT_PICKER_INJECTION_KEY, {
      slots,
      pickerNs: ns,
    })

    const commonPicker = ref<InstanceType<typeof CommonPicker>>()
    const refProps: DatePickerExpose = {
      focus: (focusStartInput = true) => {
        commonPicker.value?.focus(focusStartInput)
      },
      handleOpen: () => {
        commonPicker.value?.handleOpen()
      },
      handleClose: () => {
        commonPicker.value?.handleClose()
      },
    }

    expose(refProps)

    const onModelValueUpdated = (val: any) => {
      emit('update:modelValue', val)
    }

    const handleQuickBtnClick = (value: (() => Dayjs) | Dayjs) => {
      const shortcutValue = isFunction(value) ? value() : value
      if (shortcutValue) {
        onModelValueUpdated(
          Array.isArray(shortcutValue)
            ? [
                dayjs(shortcutValue[0]).locale(lang.value),
                dayjs(shortcutValue[1]).locale(lang.value),
              ]
            : dayjs(shortcutValue).locale(lang.value)
        )
      }
    }

    const format = computed(
      () =>
        props.format ??
        (DEFAULT_FORMATS_DATEPICKER[props.type] || DEFAULT_FORMATS_DATE)
    )

    const shortcutBtns = computed<any[]>(() =>
      (props.shortcuts || []).filter((shortcut: any) => shortcut.button)
    )

    const isSameValue = (
      a: string | number | Date | undefined,
      b: string | number | Date | undefined
    ): boolean => {
      if (!a || !b) return false
      return format.value
        ? dayjs(a).format(format.value) === dayjs(b).format(format.value)
        : dayjs(a).valueOf() === dayjs(b).valueOf()
    }

    const highlightButtonText = computed(() => {
      if (!props.highlightButton || !shortcutBtns.value.length) return ''
      const button: any = shortcutBtns.value.find((btn: any) => {
        const btnValue = isFunction(btn.value) ? btn.value() : btn.value
        if (Array.isArray(btnValue))
          return btnValue.every((item, index) => {
            if (!Array.isArray(props.modelValue)) return false
            return isSameValue(
              item,
              props.modelValue
                ? (props.modelValue as Array<any>)[index]
                : props.modelValue
            )
          })
        if (Array.isArray(props.modelValue)) return false
        return isSameValue(btnValue, props.modelValue)
      })
      return button?.text
    })

    return () => {
      // since props always have all defined keys on it, {format, ...props} will always overwrite format
      // pick props.format or provide default value here before spreading

      const Component = getPanel(props.type)

      const CommonPickerVNode = (
        <CommonPicker
          {...props}
          format={format.value}
          type={props.type}
          ref={commonPicker}
          onUpdate:modelValue={onModelValueUpdated}
        >
          {{
            default: (scopedProps: /**FIXME: remove any type */ any) => (
              <Component {...scopedProps} />
            ),
            'range-separator': slots['range-separator'],
          }}
        </CommonPicker>
      )

      return shortcutBtns.value.length ? (
        <div class={nsDate.m('shortcut-button')}>
          <el-button-group size={props.size}>
            {shortcutBtns.value.map((btn: any) => (
              <el-button
                class={{ 'is-focus': btn.text === highlightButtonText.value }}
                plain
                onClick={() => {
                  handleQuickBtnClick(btn.value)
                }}
              >
                {btn.text}
              </el-button>
            ))}
          </el-button-group>
          {CommonPickerVNode}
        </div>
      ) : (
        CommonPickerVNode
      )
    }
  },
})
